import type {SxProps, Theme} from '@mui/material';

export const muiAppBarSx = (hideTopNavi: boolean,
                            showTopNavi: boolean): SxProps<Theme> => ({
    top: hideTopNavi ? 0 : showTopNavi ? 52 : 0,
    height: {xs: 70, lg: 72},
    display: "flex",
    paddingLeft: "0!important",
    paddingRight: "0!important",
    paddingTop: 0,
    flexDirection: "column",
    alignContent: "center",
    justifyContent: "center",
    backgroundColor: "var(--background-white)",
    zIndex: 1100,
    color: (theme) => theme.palette.text.primary,
    boxShadow: "none",
    borderTop: "1px solid #e0e0e0",
    borderBottom: "1px solid #e0e0e0",
});

export const toolbarButtonSx: SxProps<Theme> = {
    display: "block",
    position: "relative",
    whiteSpace: "nowrap",
    flexShrink: 1,
    minWidth: 0,
    paddingX: 2,
    "&::after": {
        content: '""',
        position: "absolute",
        left: "0",
        right: "0",
        bottom: "-4px",
        height: "4px",
        marginX: "auto",
        width: "calc(100% - 16px)",
        backgroundColor: (theme) => theme.palette.primary.main,
        transform: "scaleX(0)",
        visibility: "hidden",
        transition: "visibility 0s linear 0.3s, transform 0.3s",
    },
    "&:hover": {
        background: "none",
        "&::after": {
            transform: "scaleX(1)",
            visibility: "visible",
            transition: "transform 0.3s",
        },
    },
};

export const toolbarButtonHoveredSx: SxProps<Theme> = {
    display: "block",
    position: "relative",
    whiteSpace: "nowrap",
    flexShrink: 1,
    minWidth: 0,
    paddingX: 2,
    background: "none",
    "&::after": {
        content: '""',
        position: "absolute",
        left: "0",
        right: "0",
        bottom: "-4px",
        height: "4px",
        marginX: "auto",
        width: "calc(100% - 16px)",
        backgroundColor: (theme) => theme.palette.primary.main,

        transform: "scaleX(1)",
        visibility: "visible",
        transition: "transform 0.3s",
    },
    "&:hover": {
        background: "none",
    },
};

export const desktopMenuSx: SxProps<Theme> = {
    display: {xs: "none", lg: "flex"},
    justifyContent: "center",
    alignItems: "center",
    flexGrow: 3,
    height: "72px",
};

export const quickAccessButtonSx: SxProps<Theme> = {
    display: "flex",
    justifyContent: "flex-end",
    width: {lg: "138px", xl: "210px"},
    whiteSpace: "nowrap",
};

export const mobileMenuButtonSx: SxProps<Theme> = {
    paddingX: 4,
    "&:hover": {
        background: "none",
    },
};

export const menuItemBoxSx = (isFlyoutSmall: boolean, theme: any) => ({
    flexGrow: 1,
    width: isFlyoutSmall ? "350px" : "33%",
    maxWidth: isFlyoutSmall ? "300px" : undefined,
    padding: "0 28px 0 32px",
    "& .openNew": {
        color: theme.palette.primary.main,
    },
});

export const baseMenuItemSx = (_theme: Theme): SxProps<Theme> => ({
    padding: 0,
    minHeight: "47px !important",
    whiteSpace: "normal",
    "&:hover": {background: "none"},
});

const underlineBase = {
    content: '""',
    position: "absolute",
    left: 0,
    bottom: "-3px",
    height: "3px",
    width: "100%",
    transformOrigin: "left",
    willChange: "transform",
    backfaceVisibility: "hidden",
    transform: "translateZ(0)",
};

export const underlineAnimatedSx: SxProps<Theme> = {
    "& a.menuItemComponent .MuiTypography-root": {
        position: "relative",
        display: "inline-block",
    },
    "& a.menuItemComponent .MuiTypography-root::after": {
        ...underlineBase,
        backgroundColor: (theme) => theme.palette.primary.main,
        transform: "scaleX(0)",
        transition: "transform 0.3s ease-out",
    },
    "&:hover a.menuItemComponent .MuiTypography-root::after": {
        transform: "scaleX(1)",
    },
    "&:hover button svg": {
        color: (theme) => theme.palette.primary.main,
    },
};

export const underlineStaticSx: SxProps<Theme> = {
    "& a.menuItemComponent .MuiTypography-root": {
        position: "relative",
        display: "inline-block",
    },
    "& a.menuItemComponent .MuiTypography-root::after": {
        ...underlineBase,
        backgroundColor: (theme) => theme.palette.primary.main,
        transform: "scaleX(1)",
    },
};

export const activeSecondLevelSx: SxProps<Theme> = {
    "& a.menuItemComponent": {
        color: (theme) => `${theme.palette.primary.main}`,
        textDecoration: "none",
    },
    "& a.menuItemComponent .MuiTypography-root": {
        position: "relative",
        display: "inline-block",
    },
    "& .MuiListItemText-root, & .MuiTypography-root": {
        color: (theme) => `${theme.palette.primary.main}`,
    },
    "& a.menuItemComponent .MuiTypography-root::after": {
        ...underlineBase,
        backgroundColor: (theme) => theme.palette.primary.main,
        transform: "scaleX(1)",
    },
    "& button svg": {
        color: (theme) => `${theme.palette.primary.main}`,
    },
};

export const activeThirdLevelSx: SxProps<Theme> = {
    backgroundColor: (theme) => `${theme.palette.primary.main}`,
    "& a.menuItemComponent, & .MuiListItemText-root, & .MuiTypography-root": {
        color: "#FFFFFF",
    },
    "&:hover": {
        backgroundColor: (theme) => `${theme.palette.primary.main}`,
    },
    "& + .MuiDivider-root": {
        backgroundColor: "transparent",
        borderColor: "transparent",
    },
};

export const chevronActiveSx: SxProps<Theme> = {
    "& button svg": {
        color: (theme) => `${theme.palette.primary.main}`,
    },
};

export const popoverSx = (isFlyoutSmall: boolean) => ({
    width: isFlyoutSmall
        ? '100%!important'
        : (theme: Theme) => `calc(100% - ${theme.spacing(3)} * 2)`,
    maxWidth: 'xl',
    ".MuiPopover-paper": {
        position: "fixed",
        display: "flex",
        left: 0,
        flexDirection: "row",
        flexWrap: "nowrap",
        width: isFlyoutSmall ? "350px" : "100%",
        maxWidth: isFlyoutSmall ? undefined : "100%",
        minHeight: isFlyoutSmall ? 0 : 503,
        maxHeight: "100%",
        padding: "24px 0",
        borderRadius: 0,
        boxShadow: "0px 4px 18px 3px #0000001F",
    },
});

export const navigationContainerSx: SxProps<Theme> = {
    display: "flex",
    justifyContent: "center",
    width: "100%",
};

export const topLevelItemWrapperSx: SxProps<Theme> = {
    display: "flex",
    alignItems: "center",
};

export const topLevelLinkContentSx: SxProps<Theme> = {
    display: "flex",
    alignItems: "center",
    gap: "0px",
    textDecoration: "none",
    padding: "0 16px",
    color: "inherit",
};

export const topLevelChevronSx = (isExpanded: boolean): SxProps<Theme> => ({
    display: "inline-flex",
    color: (theme) => theme.palette.text.primary,
    transition: "color 0.2s ease, transform 0.3s ease",
    transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
    "a:hover &": {
        color: (theme) => theme.palette.primary.main,
    },
});

export const mobileMenuWrapperSx: SxProps<Theme> = {
    display: { xs: "flex", lg: "none" },
};

export const toolbarSx = {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: {lg: 'wrap'},
    flexGrow: 1,
    padding: '0!important',
    maxWidth: 'xl',
};
